import {
  Box,
  IconButton,
  CardActions,
  Card as MuiCard,
  CardActionsProps,
  CardProps as MuiCardProps,
  Modal,
} from "@mui/material";
import { Icon } from "../Icon";
import { uniqueId } from "lodash-es";
import Draggable from "react-draggable";
import { forwardRef, useRef, useState } from "react";
import { CardImage, CardImageProps } from "../CardImage";
import { COLOR_CONSTANTS } from "../../constants/styles";
import { CardHeader, CardHeaderProps } from "../CardHeader";
import { MapImageViewer } from "../MapImageViewer";

export type CardSize = "small" | "medium";

/**
 * 드래그 가능한 이미지/지도 카드 컴포넌트의 props.
 * 크기 토큰별 폭/높이: small=120×112px, medium=272×308px.
 * 단순 썸네일 목록은 {@link ImageCard} 를 사용하고, 프로젝트 상세 이미지의 부동 뷰어는 {@link FloatImageViewer} 를 쓴다.
 */
export interface CardProps extends Omit<MuiCardProps, "children"> {
  onClickImage?: (fileName: string) => void;
  fileName?: string;
  size?: CardSize;
  bookmarked?: boolean;
  selected?: boolean;
  onClose?: () => void;
  onFullScreen?: () => void;
  onToggleBookmark?: () => void;
  /** 썸네일 영역으로 위임할 `CardImage` props */
  imageCardThumbnailProps?: Omit<CardImageProps, "size" | "ref">;
  cardActionsProps?: Omit<CardActionsProps, "children">;
  /** 헤더 영역으로 위임할 `CardHeader` props (fileName/size 는 Card 가 결정) */
  headerProps?: Omit<CardHeaderProps, "fileName" | "size">;
  draggable?: boolean;
  /** true 시 썸네일 영역을 `MapImageViewer` 로 교체 — 이미지 대신 지도 뷰 표시 */
  useMapView?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      size = "small",
      selected = false,
      onClickImage,
      bookmarked,
      fileName = "fileName",
      onFullScreen,
      onClose,
      onToggleBookmark,
      headerProps,
      imageCardThumbnailProps,
      cardActionsProps,
      draggable = true,
      useMapView = false,
    },
    ref
  ) => {
    const isSmall = size === "small";
    const cardIdRef = useRef<string>(uniqueId("card-"));
    const headerId = `${cardIdRef.current}-header`;
    const cancelId = `${cardIdRef.current}-cancel`;
    const [isFullScreen, setIsFullScreen] = useState(false);

    const handleFullScreen = () => {
      setIsFullScreen(true);
      onFullScreen?.();
    };

    const handleCloseFullScreen = () => {
      setIsFullScreen(false);
    };

    const cardContent = (
      <MuiCard
        ref={ref}
        sx={{
          ...card,
          maxHeight: isSmall ? "112px" : "308px",
          width: isSmall ? "120px" : "272px",
          outline: selected
            ? `2px solid ${COLOR_CONSTANTS.primary.main}`
            : "none",
        }}
      >
        <CardHeader
          onClose={onClose}
          size={size}
          fileName={fileName}
          className={headerId}
          dragCancelId={cancelId}
          sx={{ position: isSmall ? "absolute" : "static" }}
          {...headerProps}
        />
        {useMapView && !isSmall && imageCardThumbnailProps?.image ? (
          <MapImageViewer
            imageUrl={imageCardThumbnailProps.image}
            height="272px"
          />
        ) : (
          <CardImage
            onClick={() => onClickImage?.(fileName)}
            size={isSmall ? size : "large"}
            {...imageCardThumbnailProps}
          />
        )}
        <CardActions sx={cardActions} {...cardActionsProps}>
          {!isSmall ? (
            <IconButton size="small" onClick={handleFullScreen}>
              <Icon name="etc/full screen" size={24} sx={exitScreenIcon} />
            </IconButton>
          ) : (
            <Box />
          )}
          <IconButton size="small" onClick={onToggleBookmark}>
            {bookmarked ? (
              <Icon name="etc/BookmarkActive" size={24} />
            ) : (
              <Icon name="etc/Bookmark" size={24} sx={bookmarkIcon} />
            )}
          </IconButton>
        </CardActions>
      </MuiCard>
    );

    const cardWithDraggable = draggable ? (
      <Draggable handle={`.${headerId}`} cancel={`.${cancelId}`}>
        {cardContent}
      </Draggable>
    ) : (
      cardContent
    );

    return (
      <>
        {cardWithDraggable}
        <Modal
          open={isFullScreen}
          onClose={handleCloseFullScreen}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "90vw",
              height: "90vh",
              bgcolor: "background.paper",
              boxShadow: 24,
              position: "relative",
            }}
          >
            <CardHeader
              size="medium"
              fileName={fileName}
              onClose={handleCloseFullScreen}
              {...headerProps}
              sx={{ position: "absolute", top: 0, left: 0, zIndex: 1000 }}
            />
            {useMapView && imageCardThumbnailProps?.image ? (
              <MapImageViewer
                imageUrl={imageCardThumbnailProps.image}
                height="100%"
              />
            ) : (
              <Box
                component="img"
                src={imageCardThumbnailProps?.image}
                alt={imageCardThumbnailProps?.alt || fileName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            )}
          </Box>
        </Modal>
      </>
    );
  }
);

Card.displayName = "Card";

const exitScreenIcon = {
  "& path": {
    strokeWidth: 1,
    strokeOpacity: 1,
    fill: COLOR_CONSTANTS.common.white_states.main,
  },
} as const;

const bookmarkIcon = {
  "& path": {
    fill: "black",
    stroke: "white",
    strokeWidth: 0.8,
    strokeOpacity: 1,
  },
} as const;

const card = {
  position: "relative",
  overflow: "hidden",
} as const;

const cardActions = {
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  position: "absolute",
  bottom: 0,
  left: 0,
  padding: "2px",
} as const;
